import { Field, FormSpy } from "react-final-form";
import { RadioField, TitlePage } from "~/components";
import {
  composeValidators,
  required,
  testRegExp
} from "~/utils/validate";
import { Divider } from "antd";
import {
  FormField,
  FormFieldBic
} from "~/pages/questionnaires/shared";
import { NamesOfFieldEnum } from "~/typings/types";
import { configTitleFieldsOfCompany, configTitleFieldsOfBank } from "../configTitleFields";
import { GET_BANK_INFO } from "~/pages/questionnaires/v1/customer/api/apiUrls";
import { V2Wizard } from "../../../../shared/wizard";
import { FullWidthRowFormContainer } from "~/pages/questionnaires/shared/form-field";
import { FloatingLabelSelect } from "~/components/floating-label-select";

import styles from "./CommonSteps.module.scss";
import { useScrollAndFocusField } from "~/hooks";
import { optionsVatModeForCompany } from "~/utils/getVatModeByOrgType";
import { useAnalyticsContext } from "~/services/analytics";
import { useState } from "react";

interface CompanyDetailsProps {
  clientId: string;
}

export const CompanyDetails = ({ clientId }: CompanyDetailsProps) => {
  const [isBankConfirmed, setIsBankConfirmed] = useState(false);

  useScrollAndFocusField(NamesOfFieldEnum.BankBic);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-company-init_step3');
  analytics?.trackStepWebvisor('webvisor_anketa_customer_step2');

  return (
    <V2Wizard.Page>
      <TitlePage name="Ваши реквизиты для вывода средств" number="3" description="Вставьте БИК банка - остальные данные мы загрузим автоматически" />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>
        <FullWidthRowFormContainer>
          <FormField
            titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.VatMode]}
            name={NamesOfFieldEnum.VatMode}
            required
            validate={composeValidators(required())}
          >
            <Field
              name={NamesOfFieldEnum.VatMode}
              subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
              validate={composeValidators(required())}
              component={FloatingLabelSelect}
              titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.VatMode]}
              required
              optionsSelect={optionsVatModeForCompany}
            />
          </FormField>
        </FullWidthRowFormContainer>


        <FullWidthRowFormContainer>
          <FormSpy>
            {propsSpy => {
              return (
                <>
                  <FormField
                    titleField="Работаем с НДС"
                    name={NamesOfFieldEnum.IsVat}
                    showError={false}
                    shouldRenderTitleAboveField
                  >
                    <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                      <Field
                        name={NamesOfFieldEnum.IsVat}
                        type="radio"
                        value="yes"
                        component={RadioField}
                      >
                        Да
                      </Field>
                      <Field
                        name={NamesOfFieldEnum.IsVat}
                        type="radio"
                        value="no"
                        component={RadioField}
                      >
                        Нет
                      </Field>
                    </div>
                  </FormField>
                  {(propsSpy.values?.[NamesOfFieldEnum.IsVat] === "yes") && (
                    <FormField
                      titleField="Ставка НДС"
                      name={NamesOfFieldEnum.Vat}
                      shouldRenderTitleAboveField
                    >
                      <div className={styles.containerRadio} style={{ marginBottom: 16 }}>
                        <Field
                          name={NamesOfFieldEnum.Vat}
                          type="radio"
                          value="5"
                          component={RadioField}
                        >
                          5%
                        </Field>
                        <Field
                          name={NamesOfFieldEnum.Vat}
                          type="radio"
                          value="7"
                          component={RadioField}
                        >
                          7%
                        </Field>
                        <Field
                          name={NamesOfFieldEnum.Vat}
                          type="radio"
                          value="10"
                          component={RadioField}
                        >
                          10%
                        </Field>
                        <Field
                          name={NamesOfFieldEnum.Vat}
                          type="radio"
                          value="22"
                          component={RadioField}
                        >
                          22%
                        </Field>
                      </div>
                    </FormField>
                  )}
                </>
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy>
            {propsSpy => {
              return (
                <FormFieldBic
                  titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.BankBic]}
                  name={NamesOfFieldEnum.BankBic}
                  required
                  validate={composeValidators(
                    required(),
                    testRegExp(/^04(\d){7}$/g, 'БИК должен начинаться на "04" и состоять из 9 цифр')
                  )}
                  configNameFieldsModalSuggestion={configTitleFieldsOfBank}
                  propsSpy={propsSpy}
                  clientId={clientId}
                  tooltip="БИК — уникальный код банка. Он состоит из девяти цифр"
                  descriptionField="Введите БИК или название банка - остальные данные заполним автоматически"
                  urlGetBankInfo={GET_BANK_INFO}
                  onBankConfirmed={setIsBankConfirmed}
                />
              );
            }}
          </FormSpy>
        </FullWidthRowFormContainer>

        {
          // Костыль для валидации поля БИК, разобраться почему не выводится ошибка валидации БИК без этого скрытого поля
        }
        <div style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, overflow: 'hidden' }}>
          <FormField
            titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.BankBic]}
            name={NamesOfFieldEnum.BankBic}
            required
            validate={composeValidators(
              required(),
              testRegExp(/^04(\d){7}$/g, 'БИК должен начинаться на "04" и состоять из 9 цифр')
            )}
          />
        </div>

        {!isBankConfirmed && (
          <FullWidthRowFormContainer>
            <FormField
              titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.BankName]}
              name={NamesOfFieldEnum.BankName}
              required
              validate={composeValidators(required())}
            />
          </FullWidthRowFormContainer>
        )}

        {isBankConfirmed && (
          <FullWidthRowFormContainer>
            <FormField
              titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.SettlementAccount]}
              name={NamesOfFieldEnum.SettlementAccount}
              maxLength={20}
              required
              validate={composeValidators(
                required(),
                (value: string) => {
                  const v = String(value || "");
                  if (!/^\d{20}$/.test(v)) return "Некорректный номер счёта";
                  if (!v.startsWith("4070")) return "Некорректный номер счёта";
                  return undefined;
                }
              )}
            />
          </FullWidthRowFormContainer>
        )}

        {!isBankConfirmed && (
          <FormField
            titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.SettlementAccount]}
            name={NamesOfFieldEnum.SettlementAccount}
            maxLength={20}
            required
            validate={composeValidators(
              required(),
              (value: string) => {
                const v = String(value || "");
                if (!/^\d{20}$/.test(v)) return "Некорректный номер счёта";
                if (!v.startsWith("4070")) return "Некорректный номер счёта";
                return undefined;
              }
            )}
          />
        )}

        {!isBankConfirmed && (
          <FormField
            titleField={configTitleFieldsOfCompany[NamesOfFieldEnum.CorrespondentAccount]}
            name={NamesOfFieldEnum.CorrespondentAccount}
            maxLength={20}
            required
            validate={composeValidators(
              required(),
              testRegExp(
                /^301(\d){17}$/g,
                'Номер корреспондентского счёта должен начинаться на "301" и состоять из 20 цифр'
              )
            )}
          />
        )}

      </div>
    </V2Wizard.Page>
  );
};
